import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useLocation } from "react-router-dom";

import { useRegistrationInstance } from "@/api/registrations";
import { useLandingStatuses } from "@/api/statuses";
import PreviewStatus from "@/components/preview-status";
import SiteLogo from "@/components/site-logo";
import { Button, Card, Divider, Stack, Text } from "@/components/ui";
import { useAnalytics } from "@/contexts/analytics-context";
import { useModalContext } from "@/contexts/modal-context";
import PlaceholderStatus from "@/features/placeholder/components/placeholder-status";
import { useRegistrationStatus } from "@/hooks";
import { cn } from "@/utils/cn";
import { config } from "@/utils/config";

import LandingFooter from "./components/landing-footer";
import SignInForm from "./components/sign-in-form";

enum FromPath {
  TV_RESET_PASSWORD = "tv-reset-password",
  TV_SIGNUP = "tv-signup",
}

// The marquee scrolls at a constant pixel-per-second rate. The animation
// duration is derived from this so the perceived speed stays the same no
// matter how many Truths the backend returns.
const MARQUEE_PIXELS_PER_SECOND = 50;

const LandingPage = () => {
  const { track } = useAnalytics();
  const { openModal } = useModalContext();
  const { isLoading } = useRegistrationInstance();
  const { isOpen } = useRegistrationStatus();
  const location = useLocation();

  const { entities: statuses, isLoading: isLoadingStatuses } =
    useLandingStatuses();

  const hasStatuses = statuses.length > 0;
  const showSkeleton = isLoadingStatuses && !hasStatuses;

  const resizeTimer = useRef<NodeJS.Timeout | undefined>(undefined);
  const marquee = useRef<HTMLDivElement>(null);
  const [marqueeDuration, setMarqueeDuration] = useState<number>();

  // Cards are rendered twice so the column loops seamlessly (the keyframes
  // translate by 50% of the column height). Every card gets a trailing `mb-4`
  // so spacing is uniform — flex `gap` would omit the gap after the last card
  // and the loop would visibly jump.
  const renderTrack = () =>
    showSkeleton
      ? Array.from({ length: 8 }).map((_, index) => (
          <div key={index} className="mb-4">
            <Card
              variant="rounded"
              className="p-0! shadow-[0_0_8px_4px_rgba(84,72,238,0.08)] dark:shadow-none"
            >
              <PlaceholderStatus />
            </Card>
          </div>
        ))
      : [...statuses, ...statuses].map((status, index) => (
          <div key={`${status.id}-${index}`} className="mb-4">
            <PreviewStatus status={status} />
          </div>
        ));

  const handleOpenRegistration = () => {
    openModal("REGISTRATION");
    track("Register Click", {
      Source: "Landing Page",
    });
  };

  const startTvRegistration = () => {
    openModal("REGISTRATION");
  };

  const startResetPassword = () => {
    openModal("RESET_USERNAME_PASSWORD");
  };

  const handleResize = () => {
    marquee.current?.classList.add("animate-pause!");

    clearTimeout(resizeTimer.current!);
    resizeTimer.current = setTimeout(() => {
      marquee.current?.classList.remove("animate-pause!");
    }, 400);
  };

  // Scale the animation duration to the scrolled distance so the marquee
  // moves at a constant speed regardless of how many Truths are rendered.
  // The track holds two identical copies and one loop translates by -50%
  // (exactly one copy), so the distance per loop is half its height. A
  // ResizeObserver keeps this correct as cards settle after load and on
  // viewport resize.
  useLayoutEffect(() => {
    const track = marquee.current;
    if (!track || !hasStatuses) return;

    const measure = () => {
      const oneCopyHeight = track.scrollHeight / 2;
      if (oneCopyHeight > 0) {
        setMarqueeDuration(oneCopyHeight / MARQUEE_PIXELS_PER_SECOND);
      }
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(track);

    return () => observer.disconnect();
  }, [hasStatuses, statuses.length]);

  useEffect(() => {
    track("Home Page View");
  }, []);

  useEffect(() => {
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (isLoading) return;

    const searchParams = new URLSearchParams(window.location.search);
    const isResettingPassword = searchParams.get("action") === "reset-password";

    if (isResettingPassword) {
      startResetPassword();
      return;
    }

    const appId = searchParams.get("id");
    const fromPath = location.state?.fromPath;
    switch (fromPath) {
      case FromPath.TV_SIGNUP:
        if (appId) {
          startTvRegistration();
        }
        break;
      case FromPath.TV_RESET_PASSWORD:
        startResetPassword();
        break;
    }
  }, [isLoading]);

  return (
    <div className="flex min-h-screen flex-col overflow-auto lg:overflow-hidden">
      <div className="mx-auto flex min-h-0 w-full max-w-360 flex-1 items-stretch justify-between overflow-auto lg:overflow-hidden">
        <div className="flex w-full items-center justify-center">
          <Stack
            space={9}
            className="w-full max-w-lg px-6 pt-10 lg:max-w-md lg:px-0 lg:pt-0"
          >
            <SiteLogo className="h-6 w-auto self-start lg:h-8" />

            <Text weight="bold" size="4xl">
              <FormattedMessage
                id="landing-page.title.line-1"
                defaultMessage="Your voice."
              />
              <br />
              <FormattedMessage
                id="landing-page.title.line-2"
                defaultMessage="Your freedom."
              />
            </Text>

            <Stack space={6}>
              <SignInForm />

              <Divider
                text={<FormattedMessage id="common.or" defaultMessage="or" />}
              />

              <Button
                block
                disabled={isLoading || !isOpen}
                onClick={handleOpenRegistration}
                variant="outline"
                data-testid="create-account"
                className="border!"
              >
                <FormattedMessage
                  id="landing-page.create-account"
                  defaultMessage="Create Account"
                />
              </Button>

              <Text theme="muted" size="xs" align="center">
                <FormattedMessage
                  id="landing-page.acceptance"
                  defaultMessage="By continuing, you agree to our {terms} and {privacy}."
                  values={{
                    terms: (
                      <a
                        key="terms"
                        href={config.links.legal.terms.href}
                        target="_blank"
                        className="font-medium underline"
                      >
                        <config.links.legal.terms.FormattedMessage />
                      </a>
                    ),
                    privacy: (
                      <a
                        key="privacy"
                        href={config.links.legal.privacy.href}
                        target="_blank"
                        className="font-medium underline"
                      >
                        <config.links.legal.privacy.FormattedMessage />
                      </a>
                    ),
                  }}
                />
              </Text>
            </Stack>
          </Stack>
        </div>
        <div className="hidden w-full max-w-180 justify-center lg:flex">
          <div className="relative h-full w-full max-w-140 overflow-hidden">
            <div
              ref={marquee}
              className={cn(
                "absolute inset-x-0 top-0 mx-6",
                hasStatuses && "animate-slide-up",
              )}
              style={
                hasStatuses && marqueeDuration
                  ? { animationDuration: `${marqueeDuration}s` }
                  : undefined
              }
            >
              {renderTrack()}
            </div>
            <div className="absolute top-0 h-60 w-full bg-linear-to-b from-white to-transparent dark:from-gray-900" />
            <div className="absolute bottom-0 h-60 w-full bg-linear-to-t from-white to-transparent dark:from-gray-900" />
          </div>
        </div>
      </div>
      <LandingFooter />
    </div>
  );
};

export default LandingPage;
